import React, { useEffect, useRef, useState } from 'react';
import Badge from './Badge';
import { useLanguage } from '../i18n/LanguageContext';
import { useHomeT } from '../i18n/strings';
import { localizeItem } from '../data/portfolio';

export default function CasePanel({ item, onClose }) {
  const { lang } = useLanguage();
  const t = useHomeT(lang);
  const d = item ? localizeItem(item, lang) : null;
  const [shotIdx, setShotIdx] = useState(0);
  const touchStartX = useRef(null);

  useEffect(() => {
    setShotIdx(0);
  }, [item?.key]);

  useEffect(() => {
    if (!item) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setShotIdx((i) => i + 1);
      if (e.key === 'ArrowLeft') setShotIdx((i) => Math.max(0, i - 1));
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [item, onClose]);

  if (!d) return null;

  const gallery = d.gallery || [];
  const safeIdx = gallery.length ? shotIdx % gallery.length : 0;
  const currentSrc = gallery[safeIdx] || d.src || d.panelImg;
  const isContain = d.fit === 'contain' || (currentSrc && currentSrc.includes('/mobile/'));

  const badgeLabel =
    {
      Product: t.badgeProduct,
      Platform: t.badgePlatform,
      Mobile: t.badgeMobile,
      Collaboration: t.badgeCollab,
      Wip: t.badgeWip,
    }[d.kind] || d.kind;

  const goPrev = () => {
    if (!gallery.length) return;
    setShotIdx((i) => (i - 1 + gallery.length) % gallery.length);
  };
  const goNext = () => {
    if (!gallery.length) return;
    setShotIdx((i) => (i + 1) % gallery.length);
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0]?.clientX ?? null;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null || gallery.length < 2) return;
    const dx = (e.changedTouches[0]?.clientX ?? touchStartX.current) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0) goNext();
    else goPrev();
  };

  return (
    <div className="gdwd-case-backdrop" onClick={onClose} role="presentation">
      <div
        className="gdwd-case"
        role="dialog"
        aria-modal="true"
        aria-labelledby="case-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="gdwd-case__close" onClick={onClose} aria-label={t.close}>
          ✕
        </button>
        <div className="gdwd-card__meta">
          <Badge kind={d.kind}>{badgeLabel}</Badge>
          {d.note ? <span className="gdwd-note">{d.note}</span> : null}
          {gallery.length > 1 ? (
            <span className="gdwd-note">
              {safeIdx + 1} / {gallery.length}
            </span>
          ) : null}
        </div>
        <h2 id="case-title">{d.title}</h2>
        <p className="gdwd-case__solves">{d.solves}</p>
        {d.about ? <p className="gdwd-case__about">{d.about}</p> : null}
        {(d.metric || d.techStack.length) ? (
          <div className="gdwd-case__facts">
            {d.metric ? (
              <div className="gdwd-case__fact">
                <div className="gdwd-case__features-label">{t.metricLabel}</div>
                <div className="gdwd-case__metric">
                  <strong>{d.metric.value}</strong>
                  <span>{d.metric.label}</span>
                </div>
              </div>
            ) : null}
            {d.techStack.length ? (
              <div className="gdwd-case__fact">
                <div className="gdwd-case__features-label">{t.techStackLabel}</div>
                <div className="gdwd-case__stack">
                  {d.techStack.map((s) => (
                    <span className="gdwd-chip" key={s}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="gdwd-case__grid">
          <div>
            {currentSrc ? (
              <div
                className={`gdwd-case__shot${isContain ? ' gdwd-case__shot--contain' : ''}`}
                onTouchStart={onTouchStart}
                onTouchEnd={onTouchEnd}
              >
                <img
                  src={currentSrc}
                  alt={`${d.title} ${safeIdx + 1}`}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : d.dashed ? (
              <div className="gdwd-map-illu" aria-hidden>
                <div
                  className="gdwd-map-illu__field"
                  style={{ left: '12%', top: '28%', width: '38%', height: '32%', transform: 'rotate(-8deg)' }}
                />
                <div
                  className="gdwd-map-illu__field"
                  style={{ left: '48%', top: '18%', width: '34%', height: '40%', transform: 'rotate(6deg)' }}
                />
                <div
                  className="gdwd-map-illu__field"
                  style={{ left: '28%', top: '52%', width: '42%', height: '28%', transform: 'rotate(3deg)' }}
                />
                <span className="gdwd-map-illu__tag" style={{ left: 16, top: 16 }}>
                  field · map
                </span>
                <span className="gdwd-map-illu__tag" style={{ right: 16, bottom: 16 }}>
                  soil · history
                </span>
              </div>
            ) : null}

            {gallery.length > 1 ? (
              <>
                <div className="gdwd-case__nav">
                  <button type="button" onClick={goPrev} aria-label="Previous">
                    ←
                  </button>
                  <button type="button" onClick={goNext} aria-label="Next">
                    →
                  </button>
                </div>
                <div className="gdwd-case__thumbs" role="tablist">
                  {gallery.map((src, i) => (
                    <button
                      type="button"
                      key={src + i}
                      className={i === safeIdx ? 'is-active' : ''}
                      onClick={() => setShotIdx(i)}
                      aria-label={`Image ${i + 1}`}
                    >
                      <img src={src} alt="" loading="lazy" decoding="async" />
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
          <div>
            <div className="gdwd-case__features-label">{t.featuresLabel}</div>
            {d.features.map((f) => (
              <div className="gdwd-case__feature" key={f}>
                <span>→</span>
                {f}
              </div>
            ))}
            <div className="gdwd-chips" style={{ margin: '18px 0' }}>
              {d.chips.map((c) => (
                <span className="gdwd-chip" key={c}>
                  {c}
                </span>
              ))}
            </div>
            {d.url ? (
              <a className="gdwd-btn gdwd-btn--primary" href={d.url} target="_blank" rel="noopener noreferrer">
                {d.cta} →
              </a>
            ) : d.cta ? (
              d.kind === 'Wip' || d.ctaColor === '#B45309' ? (
                <a className="gdwd-btn gdwd-btn--ink" href="mailto:info@gdwd.lv">
                  {d.cta} →
                </a>
              ) : d.ctaMuted ? (
                <span className="gdwd-card__cta gdwd-card__cta--muted">{d.cta}</span>
              ) : (
                <span className="gdwd-card__cta">{d.cta}</span>
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
